import type { Vec3 } from '../types/GameState.js';
import { GRAVITY } from '../constants.js';

/**
 * Compute the next position and velocity of a projectile after dt seconds.
 * Standard parabolic ballistics with adjustable gravity.
 */
export function stepProjectile(
  position: Vec3,
  velocity: Vec3,
  gravityMultiplier: number,
  dt: number
): { position: Vec3; velocity: Vec3 } {
  const g = GRAVITY * gravityMultiplier;
  return {
    position: {
      x: position.x + velocity.x * dt,
      y: position.y + velocity.y * dt - 0.5 * g * dt * dt,
      z: position.z + velocity.z * dt,
    },
    velocity: {
      x: velocity.x,
      y: velocity.y - g * dt,
      z: velocity.z,
    },
  };
}

/**
 * Given a fire angle (pitch from horizontal), azimuth (yaw from +X axis), and power (speed),
 * compute the initial velocity vector.
 */
export function computeFireVelocity(
  pitch: number,
  yaw: number,
  power: number
): Vec3 {
  const cosPitch = Math.cos(pitch);
  return {
    x: cosPitch * Math.sin(yaw) * power,
    y: Math.sin(pitch) * power,
    z: cosPitch * Math.cos(yaw) * power,
  };
}

/**
 * Predict the full trajectory of a projectile (for trajectory preview line).
 * Returns an array of positions sampled at the given time step.
 * Stops when y < minY or maxSteps reached.
 */
export function predictTrajectory(
  startPos: Vec3,
  velocity: Vec3,
  gravityMultiplier: number,
  dt: number = 0.05,
  maxSteps: number = 300,
  minY: number = -10
): Vec3[] {
  const points: Vec3[] = [{ ...startPos }];
  let pos = { ...startPos };
  let vel = { ...velocity };

  for (let i = 0; i < maxSteps; i++) {
    const result = stepProjectile(pos, vel, gravityMultiplier, dt);
    pos = result.position;
    vel = result.velocity;
    points.push({ ...pos });
    if (pos.y < minY) break;
  }

  return points;
}

/**
 * Calculate distance between two 3D points.
 */
export function distance3D(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Calculate distance on the XZ plane (ignoring height).
 */
export function distanceXZ(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dz * dz);
}

/**
 * Normalize a Vec3 to unit length.
 */
export function normalize(v: Vec3): Vec3 {
  const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
  if (len === 0) return { x: 0, y: 0, z: 0 };
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}

/**
 * Linear interpolation between two Vec3.
 */
export function lerpVec3(a: Vec3, b: Vec3, t: number): Vec3 {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  };
}

/**
 * Clamp a number between min and max.
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Linear interpolation between two numbers.
 */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
